import './style.css';
import * as THREE from 'three';
import { unlockAudioOnFirstClick } from './audio/engine';
import { GAME_CONFIG } from './config/gameConfig';
import { createEffects } from './scene/effects';
import { buildEnvironment } from './scene/environment';
import { buildFieldMeshes, updateFieldHighlights } from './scene/fields';
import { addLights, createSceneContext, observeResize } from './scene/setup';
import { dispatchManualCommand } from './simulation/commands';
import { setAutoPilotEnabled, syncAutopilotUi } from './simulation/autopilot';
import { triggerReset } from './simulation/reset';
import { createSimulation } from './simulation/update';
import { baseStationPos, createInitialState } from './state/gameState';
import type { CommandTool } from './state/types';
import {
  closeFieldActionMenu,
  openFieldActionMenu,
  syncFieldActionMenu,
  updateFieldActionMenuPosition,
} from './ui/fieldActionMenu';
import { bindFieldInteraction } from './ui/input';
import { updateHTML_UI } from './ui/sidebar';
import { createVehicleMeshes } from './vehicles/meshes';

function syncHudFromConfig(): void {
  const goal = document.getElementById('cash-goal');
  if (goal) {
    goal.textContent = `Cél: Elérni a $${GAME_CONFIG.economy.winCash}-t az eladásból!`;
  }

  const hint = document.getElementById('autopilot-hint');
  if (hint) {
    const speedPct = Math.round(GAME_CONFIG.autopilot.speedMultiplier * 100);
    hint.textContent = `Bekapcsolva az egységek automatikusan munkába állnak (${speedPct}% sebességen).`;
  }
}

unlockAudioOnFirstClick();
syncHudFromConfig();

const state = createInitialState();
const sceneCtx = createSceneContext();
const { scene, camera, renderer, controls } = sceneCtx;
syncAutopilotUi(state);

addLights(scene);
const env = buildEnvironment(scene, baseStationPos);
const effects = createEffects(scene);
buildFieldMeshes(scene, state.fields);
createVehicleMeshes(scene, state);

const refreshHighlights = (): void => {
  updateFieldHighlights(state.fields, state.hoveredFieldId, state.selectedFieldId);
};

const deselectField = (): void => {
  state.selectedFieldId = null;
  closeFieldActionMenu();
  refreshHighlights();
};

const onDispatch = (fieldId: number, forceUnit?: CommandTool | null): void => {
  dispatchManualCommand(state, sceneCtx, fieldId, forceUnit ?? null);
  deselectField();
};

const refreshUi = (): void => updateHTML_UI(state, onDispatch);

const onMenuAction = (fieldId: number, unit: Exclude<CommandTool, 'auto'>): void => {
  onDispatch(fieldId, unit);
  refreshUi();
};

const selectField = (fieldId: number): void => {
  if (state.phase !== 'playing') {
    deselectField();
    return;
  }

  const field = state.fields.find((f) => f.id === fieldId);
  if (!field || field.dead) {
    deselectField();
    return;
  }

  state.selectedFieldId = fieldId;
  refreshHighlights();
  openFieldActionMenu(sceneCtx, field, onMenuAction);
};

bindFieldInteraction(sceneCtx, state, {
  onHoverChange: () => {
    refreshHighlights();
  },
  onFieldSelect: selectField,
  onFieldDeselect: deselectField,
});

document.getElementById('autopilot-toggle')?.addEventListener('change', (event) => {
  const checked = (event.target as HTMLInputElement).checked;
  setAutoPilotEnabled(state, checked);
});

const updateSimulation = createSimulation({
  state,
  sceneCtx,
  env,
  effects,
  onDispatch,
});

refreshUi();

const timer = new THREE.Timer();
timer.connect(document);

const visualTarget = new THREE.Vector3();

function animate(timestamp?: number): void {
  requestAnimationFrame(animate);
  if (renderer) {
    timer.update(timestamp);
    const dt = timer.getDelta();
    if (controls) controls.update();
    if (controls) {
      visualTarget.copy(controls.target);
    } else {
      visualTarget.set(camera.position.x, 0, camera.position.z);
    }
    env.updateVisualWorld(visualTarget);
    updateSimulation(dt);
    syncFieldActionMenu(sceneCtx, state, onMenuAction);
    updateFieldActionMenuPosition(sceneCtx);
    renderer.render(scene, camera);
  }
}

animate();
observeResize(sceneCtx);

const doReset = (): void => {
  deselectField();
  triggerReset(state, sceneCtx, onDispatch);
};

document.getElementById('reset-btn')?.addEventListener('click', doReset);
document.getElementById('end-screen-restart')?.addEventListener('click', doReset);
